<template>
  <div v-if="aberto" class="modal-grafico-sala">
    <div class="modal-grafico-sala__overlay" @click.self="fechar">
      <div class="modal-grafico-sala__container">
        <header class="modal-grafico-sala__header">
          <span class="modal-grafico-sala__title">Gráfico da Sala: {{ nomeSala || 'sala' }}</span>
          <button type="button" class="modal-grafico-sala__close" @click="fechar">&times;</button>
        </header>

        <div class="modal-grafico-sala__tabs">
          <button
            type="button"
            class="modal-grafico-sala__tab"
            :class="{ 'modal-grafico-sala__tab--active': abaAtiva === 'primario' }"
            @click="abaAtiva = 'primario'"
          >
            Primário
          </button>
          <button
            type="button"
            class="modal-grafico-sala__tab"
            :class="{ 'modal-grafico-sala__tab--active': abaAtiva === 'secundario' }"
            @click="abaAtiva = 'secundario'"
          >
            Secundário
          </button>
        </div>

        <div class="modal-grafico-sala__body">
          <div v-if="loading" class="modal-grafico-sala__state">Carregando gráfico...</div>
          <div v-else-if="loadFailed" class="modal-grafico-sala__state modal-grafico-sala__state--error">
            Não foi possível carregar os dados do gráfico.
          </div>
          <div v-else-if="!hasData" class="modal-grafico-sala__state">
            Nenhum dado de conexão encontrado para este participante.
          </div>
          <template v-else>
            <apexchart
              type="line"
              height="360"
              :options="chartOptions"
              :series="chartSeries"
            />

            <div class="modal-grafico-sala__cards">
              <article class="modal-grafico-sala__card modal-grafico-sala__card--latency">
                <span class="modal-grafico-sala__card-icon" aria-hidden="true">〰</span>
                <div>
                  <h3>Latência (ms)</h3>
                  <p>Tempo que os dados levam para viajar entre seu dispositivo e o servidor. Quanto menor, melhor.</p>
                </div>
              </article>
              <article class="modal-grafico-sala__card modal-grafico-sala__card--connection">
                <span class="modal-grafico-sala__card-icon" aria-hidden="true">⌁</span>
                <div>
                  <h3>Nível de Conexão</h3>
                  <p>Qualidade da conexão de rede em uma escala de 1 a 3. Quanto maior, melhor.</p>
                </div>
              </article>
            </div>

            <p class="modal-grafico-sala__footer">Os dados são atualizados em tempo real.</p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import salasService from '../service/salas.service';
import { showErrorToast } from '../service/toast.service';
import { buildChartData } from '../utils/grafico-sala.utils';

export default {
  name: 'ModalGraficoSala',

  components: {
    apexchart: VueApexCharts
  },

  props: {
    aberto: {
      type: Boolean,
      required: true
    },
    salaId: {
      type: Number,
      default: null
    },
    nomeSala: {
      type: String,
      default: ''
    },
    uidPrimario: {
      type: String,
      default: ''
    },
    uidSecundario: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      loading: false,
      loadFailed: false,
      abaAtiva: 'primario',
      dadosPrimario: [],
      dadosSecundario: []
    };
  },

  computed: {
    chartData() {
      const records = this.abaAtiva === 'primario'
        ? this.dadosPrimario
        : this.dadosSecundario;

      return buildChartData(records, this.salaId);
    },

    hasData() {
      return this.chartData.points.length > 0;
    },

    chartSeries() {
      return [
        {
          name: 'Latência (ms)',
          type: 'line',
          data: this.chartData.latency
        },
        {
          name: 'Nível de Conexão',
          type: 'line',
          data: this.chartData.connection
        }
      ];
    },

    chartOptions() {
      return {
        chart: {
          toolbar: { show: true },
          zoom: { enabled: true }
        },
        colors: ['#3b82f6', '#22c55e'],
        stroke: {
          width: [3, 3],
          curve: 'smooth'
        },
        markers: {
          size: 4
        },
        dataLabels: {
          enabled: true,
          enabledOnSeries: [0, 1]
        },
        legend: {
          position: 'top',
          horizontalAlign: 'center'
        },
        xaxis: {
          type: 'numeric',
          title: {
            text: 'Tempo (minutos)'
          }
        },
        yaxis: [
          {
            title: {
              text: 'Latência (ms)'
            },
            min: 0
          },
          {
            opposite: true,
            title: {
              text: 'Nível de Conexão'
            },
            min: 1,
            max: 3
          }
        ],
        grid: {
          borderColor: '#e5e7eb',
          strokeDashArray: 4
        }
      };
    }
  },

  watch: {
    aberto(novoValor) {
      if (novoValor && this.salaId) {
        this.carregarDados();
      } else if (!novoValor) {
        this.limparDados();
      }
    }
  },

  methods: {
    async carregarDados() {
      this.loading = true;
      this.loadFailed = false;
      this.abaAtiva = 'primario';

      try {
        const requests = [];

        if (this.uidPrimario) {
          requests.push(salasService.getInfoByUID(this.uidPrimario));
        } else {
          requests.push(Promise.resolve([]));
        }

        if (this.uidSecundario) {
          requests.push(salasService.getInfoByUID(this.uidSecundario));
        } else {
          requests.push(Promise.resolve([]));
        }

        const [dadosPrimario, dadosSecundario] = await Promise.all(requests);
        this.dadosPrimario = dadosPrimario || [];
        this.dadosSecundario = dadosSecundario || [];
      } catch (err) {
        showErrorToast('Não foi possível carregar os dados do gráfico.');
        this.loadFailed = true;
      } finally {
        this.loading = false;
      }
    },

    fechar() {
      this.$emit('fechar');
    },

    limparDados() {
      this.loading = false;
      this.loadFailed = false;
      this.abaAtiva = 'primario';
      this.dadosPrimario = [];
      this.dadosSecundario = [];
    }
  }
};
</script>

<style scoped src="./ModalGraficoSala.css"></style>
